import { motion } from 'framer-motion'
import { useWallet } from '../../../state/wallet'
import { useTransactions } from '../../../state/transactions'
import { useAuth } from '../../../state/auth'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '../../../components/ui/dialog'
import { Badge } from '../../../components/ui/badge'
import { 
  Wallet, 
  DollarSign, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Clock,
  CheckCircle,
  XCircle,
  Receipt,
  Calendar,
  AlertCircle,
  Copy,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { TransactionFilters } from '../../../models/transaction'
import { useTranslation } from '../../../hooks/useTranslation'
import { useDesignSystem } from '@/hooks/useDesignSystem'
import { LoadingSpinner } from '@/components/ui/loading'

const getTransactionIcon = (type: string) => {
  switch (type) {
    case 'commission':
      return <ArrowDownLeft className="h-4 w-4" />
    case 'withdrawal':
      return <ArrowUpRight className="h-4 w-4" />
    case 'purchase':
      return <Receipt className="h-4 w-4" />
    default:
      return <DollarSign className="h-4 w-4" />
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-red-500" />
    default:
      return <Clock className="h-4 w-4 text-slate-500" />
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
    case 'failed':
    case 'cancelled':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
    default:
      return 'bg-muted text-foreground dark:bg-muted dark:text-muted-foreground'
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatAmount = (amount: string | number, type: string) => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  const formattedAmount = numAmount.toFixed(2)
  if (type === 'commission') return `+$${formattedAmount}`
  if (type === 'withdrawal' || type === 'purchase') return `-$${formattedAmount}`
  return `$${formattedAmount}`
}

export default function WalletPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { animations, patterns } = useDesignSystem()
  const navigate = useNavigate()
  
  const getTransactionDescription = (transaction: { description?: string; type: string; id: string | number }) => {
    if (transaction.description) return transaction.description
    if (transaction.type === 'commission') return t('pages.wallet.referralCommission')
    if (transaction.type === 'withdrawal') return t('pages.wallet.withdrawalRequest')
    return t('pages.wallet.transactionId', { id: transaction.id })
  }
  const { 
    wallet, 
    dataIsLoading: walletLoading, 
    dataError: walletError, 
    requestWithdrawal,
    requestWithdrawalLoading,
    requestWithdrawalError,
    requestWithdrawalSuccess,
    refetch: refetchWallet
  } = useWallet()

  const filters: TransactionFilters = { limit: 10, type: 'commission' }
  const { 
    transactions, 
    dataIsLoading: transactionsLoading, 
    dataError: transactionsError,
    refetch: refetchTransactions
  } = useTransactions(filters)

  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false)
  const [withdrawalAmount, setWithdrawalAmount] = useState('')

  // Reset form and close modal on successful withdrawal
  useEffect(() => {
    if (requestWithdrawalSuccess) {
      setIsWithdrawModalOpen(false)
      setWithdrawalAmount('')
      refetchWallet()
      refetchTransactions()
    }
  }, [requestWithdrawalSuccess, refetchWallet, refetchTransactions])

  const handleWithdrawal = () => {
    if (!withdrawalAmount || !user?.crypto_wallet_address) return
    
    const withdrawalData = {
      amount: withdrawalAmount,
    }
    
    requestWithdrawal(withdrawalData)
  }

  const isLoading = walletLoading || transactionsLoading
  const hasError = walletError || transactionsError

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <LoadingSpinner size="lg" text={t('pages.wallet.loadingWallet')} />
      </div>
    )
  }

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
          <XCircle className="h-8 w-8 text-red-600" />
        </div>
        <div className="text-center">
          <h3 className={'text-lg font-semibold mb-2 ' + patterns.text.heading}>
            {t('pages.wallet.loadError')}
          </h3>
          <p className={patterns.text.muted + ' mb-4'}>
            {t('pages.wallet.loadErrorMessage')}
          </p>
          <Button 
            onClick={() => {
              refetchWallet()
              refetchTransactions()
            }} 
            variant="outline"
            className="bg-white hover:bg-muted dark:bg-card dark:hover:bg-muted"
          >
            {t('common.buttons.refresh')}
          </Button>
        </div>
      </div>
    )
  }

  const walletBalance = wallet ? parseFloat(wallet.balance) : 0

  return (
    <motion.div 
      initial="hidden" 
      animate="visible" 
      variants={animations.component.staggerContainer}
      className="space-y-8"
    >
      {/* Header Section */}
      <motion.div variants={animations.content.header} className="text-center space-y-4">
        <div className={'inline-flex items-center px-4 py-2 border border-border bg-muted/50 ' + patterns.radius.full}>
          <span className="text-sm font-medium text-primary">
            {t('pages.wallet.financialHub')}
          </span>
        </div>
        <h1 className={'text-4xl lg:text-5xl font-bold ' + patterns.text.heading}>
          {t('pages.wallet.title')}
        </h1>
        <p className={'text-lg max-w-2xl mx-auto ' + patterns.text.muted}>
          {t('pages.wallet.subtitle')}
        </p>
      </motion.div>

      {/* Balance Card */}
      <motion.div variants={animations.stats.statsCard}>
        <Card className={'border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card text-white'}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('pages.wallet.balance')}</CardTitle>
            <div className={patterns.utils.cx('h-8 w-8 flex items-center justify-center', patterns.radius.full, 'bg-white/10')}>
              <Wallet className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              ${walletBalance.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('pages.wallet.availableForWithdrawal')}
            </p>
            <div className='flex items-center flex-row mt-2'>
              <button 
                onClick={() => setIsWithdrawModalOpen(true)}
                className='flex flex-row items-center hover:bg-muted group cursor-pointer select-none rounded-full pr-4 transition-colors duration-200'
              >
                <div className={patterns.utils.cx('rounded-full p-2.5 bg-primary border-t-1 border-b-1 border-transparent group-hover:border-r-1 group-hover:border-r-primary-foreground/20 group-hover:border-t-1 group-hover:border-t-primary-foreground/20 group-hover:border-b-1 group-hover:border-b-primary-foreground/20 transition-colors duration-200', patterns.radius.full)}>
                  <ArrowUpRight strokeWidth={4} className='text-primary-foreground h-4 w-4 transition-colors duration-200' />
                </div>
                <span className='ml-2 text-sm font-bold'>{t('pages.wallet.withdraw')}</span>
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Wallet Address Card */}
      {user?.crypto_wallet_address && (
        <motion.div variants={animations.content.section}>
          <Card className={patterns.card.base}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                {t('pages.wallet.walletAddress')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={patterns.utils.cx('flex items-center justify-between p-4', patterns.radius.xl, 'bg-muted/50')}>
                <div className="flex-1 mr-4">
                  <p className={patterns.text.muted + ' mb-1 text-sm'}>
                    {t('pages.wallet.yourWalletAddress')}
                  </p>
                  <code className="text-sm font-mono bg-background px-2 py-1 rounded border break-all">
                    {user.crypto_wallet_address}
                  </code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(user.crypto_wallet_address || '')}
                  className="flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  {t('common.buttons.copy')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Transaction History */}
      <motion.div variants={animations.content.section}>
        <Card className={patterns.card.base}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              {t('pages.wallet.transactions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={animations.component.staggerContainer}
              className="space-y-3"
            >
              {transactions && transactions.length > 0 ? (
                transactions.slice(0, 5).map((transaction) => (
                  <motion.div 
                    key={transaction.id} 
                    variants={animations.component.listItem}
                    whileHover={{ x: 4, transition: { duration: 0.15 } }}
                    className={patterns.utils.cx('flex items-center justify-between p-4 border border-border bg-muted/50 hover:shadow-md transition-all duration-200', patterns.radius.xl)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={patterns.utils.cx('p-2.5', patterns.radius.full,
                        transaction.type === 'commission' 
                          ? 'bg-green-500/20 text-green-500' 
                          : transaction.type === 'withdrawal'
                          ? 'bg-red-500/20 text-red-500'
                          : 'bg-blue-500/20 text-blue-500'
                      )}>
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div>
                        <div className={'font-medium ' + patterns.text.heading}>
                          {getTransactionDescription(transaction)}
                        </div>
                        <div className={'text-sm flex items-center gap-2 ' + patterns.text.muted}>
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(transaction.created_at)}</span>
                          <span>•</span>
                          <Badge 
                            variant="secondary"
                            className={patterns.utils.cx(getStatusColor(transaction.status), 'flex items-center gap-1')}
                          >
                            {getStatusIcon(transaction.status)}
                            {transaction.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className={patterns.utils.cx('font-semibold text-lg', 
                      transaction.type === 'commission' ? 'text-green-500' : 'text-red-500'
                    )}>
                      {formatAmount(transaction.amount, transaction.type)}
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-12">
              <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <DollarSign className="h-8 w-8 text-muted-foreground" />
              </div>
                  <h3 className={'text-lg font-semibold mb-2 ' + patterns.text.heading}>
                    {t('pages.wallet.noTransactions')}
                  </h3>
                  <p className={patterns.text.muted}>
                    {t('pages.wallet.startEarning')}
                  </p>
                </div>
              )}
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Withdrawal Dialog */}
      <Dialog open={isWithdrawModalOpen} onOpenChange={setIsWithdrawModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-white">{t('pages.wallet.requestWithdrawal')}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('pages.wallet.withdrawalDescription')}
            </DialogDescription>
          </DialogHeader>
        <div className="space-y-6">
          <div className={'text-sm ' + patterns.text.muted}>
            {t('pages.wallet.availableBalance')}: <span className="font-semibold text-green-500">${walletBalance.toFixed(2)}</span>
          </div>

          {!user?.crypto_wallet_address && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span className="text-sm">
                {t('pages.wallet.walletAddressRequired', {
                  link: (
                    <button
                      onClick={() => {
                        setIsWithdrawModalOpen(false)
                        navigate({ to: '/dashboard/profile' })
                      }}
                      className="underline font-medium hover:text-yellow-900 dark:hover:text-yellow-300"
                    >
                      {t('pages.wallet.clickHere')}
                    </button>
                  )
                })}
              </span>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              {t('pages.wallet.amount')}
            </label>
            <Input
              type="number"
              placeholder={t('pages.wallet.enterAmount')}
              value={withdrawalAmount}
              onChange={(e) => setWithdrawalAmount(e.target.value)}
              className="bg-background border-border"
              disabled={!user?.crypto_wallet_address}
            />
          </div>

          {requestWithdrawalError ? (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">
                {typeof requestWithdrawalError === 'object' && requestWithdrawalError && 'message' in requestWithdrawalError 
                  ? String(requestWithdrawalError.message) 
                  : t('pages.wallet.withdrawalError')}
              </span>
            </div>
          ) : null}

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setIsWithdrawModalOpen(false)}
              className="flex-1"
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button 
              onClick={handleWithdrawal}
              disabled={
                !withdrawalAmount || 
                !user?.crypto_wallet_address || 
                parseFloat(withdrawalAmount) > walletBalance ||
                parseFloat(withdrawalAmount) < 5 ||
                requestWithdrawalLoading
              }
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {requestWithdrawalLoading ? t('pages.wallet.processing') : t('pages.wallet.submitRequest')}
            </Button>
          </div>
        </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}
